import { useState, useMemo } from 'react';
import { CheckCircle } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { EmptyState } from '../../components/common/EmptyState';
import { PROJECT_STATUSES, COMPLETED_STATUSES, TH, TD } from './config';
import type { ProjectRow } from './hooks';

interface Props {
  projects: ProjectRow[];
  onSelect: (p: ProjectRow) => void;
}

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 0 })}`; }

export function CompletedTab({ projects, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(25);

  const completed = useMemo(() => {
    let list = projects.filter(p => COMPLETED_STATUSES.includes(p.status));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || (p.client_display ?? '').toLowerCase().includes(q));
    }
    return list.sort((a, b) => (b.actual_end_date ?? b.created_at).localeCompare(a.actual_end_date ?? a.created_at));
  }, [projects, search]);

  const groups = useMemo(() => {
    const map = new Map<string, ProjectRow[]>();
    for (const p of completed.slice(0, visibleCount)) {
      const d = p.actual_end_date ?? p.created_at;
      const key = d.substring(0, 7);
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([month, rows]) => ({
      label: new Date(month + '-15').toLocaleDateString('en-CA', { month: 'long', year: 'numeric' }),
      rows,
    }));
  }, [completed, visibleCount]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search completed projects..."
          style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 240 }} />
        <span style={{ fontSize: 13, color: palette.g400 }}>{completed.length} project{completed.length !== 1 ? 's' : ''}</span>
      </div>

      {completed.length === 0 ? (
        <EmptyState icon={<CheckCircle size={22} />} title="No completed projects yet" subtitle="Completed projects will appear here." />
      ) : (
        <div>
          {groups.map(g => (
            <div key={g.label} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 0', borderBottom: `1px solid ${palette.g150}` }}>
                {g.label} ({g.rows.length})
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={TH}>Name</th><th style={TH}>Client</th><th style={TH}>End Date</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Budget</th><th style={TH}>Status</th>
                </tr></thead>
                <tbody>
                  {g.rows.map(p => {
                    const st = PROJECT_STATUSES[p.status] ?? PROJECT_STATUSES.complete;
                    const hasTrust = p.gps_verified && p.has_before_photos && p.has_after_photos && p.client_confirmed;
                    return (
                      <tr key={p.id} onClick={() => onSelect(p)} style={{ cursor: 'pointer' }}
                        onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <td style={{ ...TD, fontWeight: 500 }}>
                          {p.name}
                          {hasTrust && <span style={{ marginLeft: 6, fontSize: 10, color: palette.green }} title="Trust Badge">&#x2713;</span>}
                        </td>
                        <td style={TD}>{p.client_display ?? '—'}</td>
                        <td style={TD}>{(p.actual_end_date ?? p.created_at).split('T')[0]}</td>
                        <td style={{ ...TD, textAlign: 'right' }}>{p.budget ? fmt$(p.budget) : '—'}</td>
                        <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: st.bg, color: st.fg }}>{st.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
          {visibleCount < completed.length && (
            <button onClick={() => setVisibleCount(v => v + 25)} style={{
              width: '100%', padding: '10px 0', borderRadius: 6, border: `1px solid ${palette.g200}`,
              background: palette.white, fontSize: 12.5, fontWeight: 500, color: palette.g600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>Load more</button>
          )}
        </div>
      )}
    </div>
  );
}
